import { getCookie, setCookie } from '@travel/utils';

import { DEVICE_ID } from '../constants/cookies';

import type { MemberItem } from '../types/member';
export const getPreviousPurchase = () => {
  const pastPurchaseJson = getCookie('pastPurchaseOnDevice');

  try {
    return pastPurchaseJson ? JSON.parse(pastPurchaseJson) : {};
  } catch (error) {
    return {};
  }
};

export const setPreviousPurchase = (pastPurchaseObj: { [key: string]: string }) => {
  try {
    const expiresDate = new Date();
    expiresDate.setFullYear(expiresDate.getFullYear() + 5);
    const cookieVal = JSON.stringify(pastPurchaseObj);
    setCookie('pastPurchaseOnDevice', cookieVal, {
      sameSite: 'strict',
      secure: true,
      path: '/',
      expires: expiresDate,
    });
  } catch (error) {
    console.error({ error });
  }
};

export const getHasPastPurchaseOnDevice = (item: MemberItem) => {
  let hasPastPurchaseOnDevice: boolean = false;
  const pastPurchaseObj = getPreviousPurchase();

  const trackId: string | undefined = item?.trackId;
  const deviceId = getCookie(DEVICE_ID);
  const pastPurchaseKey = trackId || deviceId;
  if (pastPurchaseKey) {
    hasPastPurchaseOnDevice = Boolean(pastPurchaseObj[pastPurchaseKey]);
  }

  return hasPastPurchaseOnDevice;
};

export const campaignPageNameSiteSection = (
  pathname: string,
): { pageName?: string; siteSection?: string } => {
  const basePath = 'contents/';
  const contentPathIndex = pathname.indexOf(basePath);

  if (contentPathIndex === -1) {
    return { pageName: undefined, siteSection: undefined };
  }
  const contentPath = pathname.substring(contentPathIndex + basePath.length);

  // Skipping the first two elements (market and language).
  const [, , siteSection, ...rest] = contentPath.split('/').filter(Boolean);

  if (!siteSection) {
    return { pageName: undefined, siteSection: undefined };
  }

  const pageName = `${siteSection}${rest.length > 0 ? `:${rest.join('/')}` : ''}`;

  return {
    pageName,
    siteSection,
  };
};

/**
 * Determines if the user is on a web or webview platform.
 * NOTE: This is not 100% reliable, as it depends on user-agent strings
 * and injected JavaScript objects which can vary.
 * @returns {'webview' | 'web'} The detected platform.
 */
export const getPlatform = (): 'webview' | 'web' => {
  const webviewSignatures = [
    /WebView/, // Standard Android WebView
    /wv\)/, // Chrome on Android when in a WebView
    /iP(hone|ad|od).*AppleWebKit(?!.*Safari)/, // iOS UIWebView/WKWebView without 'Safari'
    /AppleWebKit\/.* (CFNetwork\/.*)/, // Another iOS WKWebView indicator
  ];

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Native apps can inject global objects into the webview's window scope.
  const hasInjectedInterface = !!(
    (window as any).Android || (window as any).webkit?.messageHandlers
  );

  if (hasInjectedInterface || webviewSignatures.some((rx) => rx.test(userAgent))) {
    return 'webview';
  }

  return 'web';
};

export const getTrackingParams = (): Record<string, string> => {
  const { pageName } = campaignPageNameSiteSection(window.location.pathname);
  const platform = getPlatform();

  const params: Record<string, string> = {
    'trv-platform': platform,
  };

  if (pageName) {
    params['trv-page'] = pageName;
  }

  return params;
};

export const isMacOs = () => navigator.platform.toUpperCase().indexOf('MAC') >= 0;
